/**
 * PublicRegistrationIntakeService
 * Anonymous (public) course registration submission
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for submitting public course registrations (no authentication required)
 */
export class PublicRegistrationIntakeService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PublicRegistrationIntakeService.DEFAULT_SERVICE_PATH,
            basePath: PublicRegistrationIntakeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Submit a public course registration
     * @param request - The registration submission payload
     * @returns Public intake receipt (reference + message)
     */
    async submit(request) {
        const response = await this.submitRaw(request);
        if (!response.data) {
            throw new Error('Failed to submit public course registration');
        }
        return response.data;
    }
    /**
     * Submit a public course registration with full response
     * @param request - The registration submission payload
     * @returns ResponseData with public intake receipt
     */
    submitRaw(request) {
        const url = this.buildUrl();
        return this.http.post(url, request);
    }
    /**
     * Submit an anonymous Pre-Marriage course registration (couple + relationship detail). The server
     * match-or-creates a member for each partner, links them into one family, and stages the extra detail
     * as course custom data. The first per-course registration shape.
     * POST api/public/registration-intakes/pre-marriage
     * @returns Public intake receipt (reference + message)
     */
    async submitPreMarriage(request) {
        const response = await this.submitPreMarriageRaw(request);
        if (!response.data) {
            throw new Error('Failed to submit Pre-Marriage registration');
        }
        return response.data;
    }
    submitPreMarriageRaw(request) {
        const url = this.buildUrl('pre-marriage');
        return this.http.post(url, request);
    }
    /**
     * Submit an anonymous Griefshare course registration (single registrant + loss/grief detail). The
     * server match-or-creates the registrant's member and stages the grief detail as course custom data.
     * POST api/public/registration-intakes/griefshare
     * @returns Public intake receipt (reference + message)
     */
    async submitGriefshare(request) {
        const response = await this.submitGriefshareRaw(request);
        if (!response.data) {
            throw new Error('Failed to submit Griefshare registration');
        }
        return response.data;
    }
    submitGriefshareRaw(request) {
        const url = this.buildUrl('griefshare');
        return this.http.post(url, request);
    }
    /**
     * Submit an anonymous DivorceCare registration (single adult + divorce detail).
     * POST api/public/registration-intakes/divorce-care
     */
    async submitDivorceCare(request) {
        const response = await this.submitDivorceCareRaw(request);
        if (!response.data) {
            throw new Error('Failed to submit DivorceCare registration');
        }
        return response.data;
    }
    submitDivorceCareRaw(request) {
        const url = this.buildUrl('divorce-care');
        return this.http.post(url, request);
    }
    /**
     * Submit an anonymous DivorceCare-for-Kids registration (parent/guardian + children).
     * POST api/public/registration-intakes/divorce-care-kids
     */
    async submitDivorceCareForKids(request) {
        const response = await this.submitDivorceCareForKidsRaw(request);
        if (!response.data) {
            throw new Error('Failed to submit DivorceCare-for-Kids registration');
        }
        return response.data;
    }
    submitDivorceCareForKidsRaw(request) {
        const url = this.buildUrl('divorce-care-kids');
        return this.http.post(url, request);
    }
    /**
     * Submit an anonymous Acts Recovery registration (single registrant + recovery detail + alt support
     * contact). The server match-or-creates the registrant's member and stages the recovery detail as
     * course custom data.
     * POST api/public/registration-intakes/acts-recovery
     */
    async submitActsRecovery(request) {
        const response = await this.submitActsRecoveryRaw(request);
        if (!response.data) {
            throw new Error('Failed to submit Acts Recovery registration');
        }
        return response.data;
    }
    submitActsRecoveryRaw(request) {
        const url = this.buildUrl('acts-recovery');
        return this.http.post(url, request);
    }
    /**
     * Submit an anonymous Marriage Discussion registration (a married couple + discussion detail). The
     * server match-or-creates a member for each partner, links them into one family, and stages the
     * discussion detail as course custom data.
     * POST api/public/registration-intakes/marriage-discussion
     */
    async submitMarriageDiscussion(request) {
        const response = await this.submitMarriageDiscussionRaw(request);
        if (!response.data) {
            throw new Error('Failed to submit Marriage Discussion registration');
        }
        return response.data;
    }
    submitMarriageDiscussionRaw(request) {
        const url = this.buildUrl('marriage-discussion');
        return this.http.post(url, request);
    }
}
PublicRegistrationIntakeService.DEFAULT_SERVICE_PATH = 'ac-courses';
PublicRegistrationIntakeService.BASE_PATH = 'api/public/registration-intakes';
//# sourceMappingURL=PublicRegistrationIntakeService.js.map